import { useEffect, useState, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Users, Calendar, Edit, Trash2, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MeetingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [meeting, setMeeting] = useState(null);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateActionDialog, setShowCreateActionDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newAction, setNewAction] = useState({
    title: '',
    description: '',
    owner_id: '',
    date_raised: new Date().toISOString().split('T')[0],
    due_date: '',
    priority: 'Medium',
    category: '',
    status: 'Not Started'
  });
  const [editForm, setEditForm] = useState({
    title: '',
    date: '',
    notes: '',
    attendees: []
  });

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  useEffect(() => {
    fetchMeetingDetail();
    fetchMeetingActions();
    fetchUsers();
    fetchCategories();
  }, [id]);

  const fetchMeetingDetail = async () => {
    try {
      const response = await axios.get(`${API}/meetings/${id}`);
      setMeeting(response.data);
      setEditForm({
        title: response.data.title,
        date: response.data.date,
        notes: response.data.notes || '',
        attendees: response.data.attendees || []
      });
    } catch (error) {
      toast.error('Failed to fetch meeting details');
    } finally {
      setLoading(false);
    }
  };

  const fetchMeetingActions = async () => {
    try {
      const response = await axios.get(`${API}/meetings/${id}/actions`);
      setActions(response.data);
    } catch (error) {
      console.error('Failed to fetch meeting actions:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data.filter(u => u.status === 'active'));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleCreateAction = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/actions`, {
        ...newAction,
        meeting_id: id
      });
      toast.success('Action created successfully');
      setShowCreateActionDialog(false);
      setNewAction({
        title: '',
        description: '',
        owner_id: '',
        date_raised: new Date().toISOString().split('T')[0],
        due_date: '',
        priority: 'Medium',
        category: '',
        status: 'Not Started'
      });
      fetchMeetingActions();
    } catch (error) {
      toast.error('Failed to create action: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateMeeting = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/meetings/${id}`, editForm);
      toast.success('Meeting updated successfully');
      setShowEditDialog(false);
      fetchMeetingDetail();
    } catch (error) {
      toast.error('Failed to update meeting: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteMeeting = async () => {
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;
    try {
      await axios.delete(`${API}/meetings/${id}`);
      toast.success('Meeting deleted successfully');
      navigate('/meetings');
    } catch (error) {
      toast.error('Failed to delete meeting: ' + (error.response?.data?.detail || error.message));
    }
  };

  const toggleAttendee = (userId) => {
    setEditForm(prev => ({
      ...prev,
      attendees: prev.attendees.includes(userId)
        ? prev.attendees.filter(id => id !== userId)
        : [...prev.attendees, userId]
    }));
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'In Progress': return <Clock className="w-4 h-4 text-blue-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'In Progress': return 'bg-blue-100 text-blue-800';
      case 'Not Started': return 'bg-gray-100 text-gray-800';
      case 'Deferred': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'Urgent': return 'bg-red-100 text-red-800';
      case 'High': return 'bg-orange-100 text-orange-800';
      case 'Medium': return 'bg-blue-100 text-blue-800';
      case 'Low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  if (!meeting) {
    return <div className="text-center py-12">Meeting not found</div>;
  }

  const attendeeNames = meeting.attendees?.map(userId => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unknown';
  }) || [];

  return (
    <div className="space-y-6 fade-in" data-testid="meeting-detail-page">
      <div className="flex items-center justify-between">
        <Link to="/meetings">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Meetings
          </Button>
        </Link>
        {isAdmin && (
          <div className="flex gap-2">
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="edit-meeting-button">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Meeting</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateMeeting} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-title">Meeting Title *</Label>
                    <Input
                      id="edit-title"
                      value={editForm.title}
                      onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                      required
                      data-testid="edit-meeting-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-date">Date *</Label>
                    <Input
                      id="edit-date"
                      type="datetime-local"
                      value={editForm.date ? editForm.date.slice(0, 16) : ''}
                      onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                      required
                      data-testid="edit-meeting-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-notes">Notes</Label>
                    <Textarea
                      id="edit-notes"
                      value={editForm.notes}
                      onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                      rows={4}
                      data-testid="edit-meeting-notes"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Attendees</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-md p-3">
                      {users.map((u) => (
                        <label 
                          key={u.id} 
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${editForm.attendees.includes(u.id) ? 'bg-blue-50' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={editForm.attendees.includes(u.id)}
                            onChange={() => toggleAttendee(u.id)}
                            className="rounded"
                          />
                          <span className="text-sm">{u.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-gradient-to-r from-blue-500 to-green-500" data-testid="save-meeting-button">
                      Save Changes
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            <Button variant="destructive" size="sm" onClick={handleDeleteMeeting} data-testid="delete-meeting-button">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        )}
      </div>

      <Card className="card-hover">
        <CardHeader>
          <CardTitle className="text-3xl">{meeting.title}</CardTitle>
          <div className="flex items-center gap-4 text-gray-600">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(meeting.date).toLocaleString('en-GB')}
            </span>
            {attendeeNames.length > 0 && (
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {attendeeNames.length} attendee{attendeeNames.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {meeting.notes && (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Notes</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{meeting.notes}</p>
              </div>
            )}
            {attendeeNames.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Attendees</h3>
                <div className="flex flex-wrap gap-2">
                  {attendeeNames.map((name, idx) => (
                    <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="card-hover">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Related Actions ({actions.length})</CardTitle>
          <Dialog open={showCreateActionDialog} onOpenChange={setShowCreateActionDialog}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-500 to-green-500" data-testid="create-action-from-meeting">
                <Plus className="w-4 h-4 mr-2" />
                Add Action
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Action from Meeting</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateAction} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="action-title">Title *</Label>
                    <Input
                      id="action-title"
                      value={newAction.title}
                      onChange={(e) => setNewAction({...newAction, title: e.target.value})}
                      required
                      data-testid="new-action-title"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="action-description">Description *</Label>
                    <Textarea
                      id="action-description"
                      value={newAction.description}
                      onChange={(e) => setNewAction({...newAction, description: e.target.value})}
                      required
                      rows={3}
                      data-testid="new-action-description"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="action-owner">Owner *</Label>
                    <Select 
                      value={newAction.owner_id} 
                      onValueChange={(value) => setNewAction({...newAction, owner_id: value})}
                    >
                      <SelectTrigger data-testid="new-action-owner">
                        <SelectValue placeholder="Select owner" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="action-category">Category *</Label>
                    <Select 
                      value={newAction.category} 
                      onValueChange={(value) => setNewAction({...newAction, category: value})}
                    >
                      <SelectTrigger data-testid="new-action-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="action-date-raised">Date Raised *</Label>
                    <Input
                      id="action-date-raised"
                      type="date"
                      value={newAction.date_raised}
                      onChange={(e) => setNewAction({...newAction, date_raised: e.target.value})}
                      required
                      data-testid="new-action-date-raised"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="action-due-date">Due Date</Label>
                    <Input
                      id="action-due-date"
                      type="date"
                      value={newAction.due_date}
                      onChange={(e) => setNewAction({...newAction, due_date: e.target.value})}
                      data-testid="new-action-due-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="action-priority">Priority</Label>
                    <Select 
                      value={newAction.priority} 
                      onValueChange={(value) => setNewAction({...newAction, priority: value})}
                    >
                      <SelectTrigger data-testid="new-action-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="action-status">Status</Label>
                    <Select 
                      value={newAction.status} 
                      onValueChange={(value) => setNewAction({...newAction, status: value})}
                    >
                      <SelectTrigger data-testid="new-action-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Not Started">Not Started</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Deferred">Deferred</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateActionDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-gradient-to-r from-blue-500 to-green-500"
                    disabled={!newAction.title || !newAction.description || !newAction.owner_id || !newAction.category}
                    data-testid="submit-action-button"
                  >
                    Create Action
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {actions.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No actions linked to this meeting</p>
          ) : (
            <div className="space-y-3">
              {actions.map((action) => (
                <Link
                  key={action.id}
                  to={`/actions/${action.id}`}
                  className="block p-4 border rounded-lg hover:bg-gray-50 transition"
                  data-testid={`action-${action.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {getStatusIcon(action.status)}
                        <span className="font-mono text-sm text-gray-500">{action.reference}</span>
                      </div>
                      <h4 className="font-medium text-gray-900">{action.title}</h4>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{action.description}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2 ml-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(action.status)}`}>
                        {action.status}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(action.priority)}`}>
                        {action.priority}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
